import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Table as TableIcon,
  Plus, Variable, ChevronDown, Trash2,
} from 'lucide-react';
import { TemplateVariable } from './tiptap/VariableExtension';
import { prepareHtmlForEditor } from '../utils/templateVars';

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-sm transition-colors ${
        active ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

export default function RichTextEditor({
  content = '',
  onChange,
  variables = [],
  onVariablesChange,
  placeholder = 'Start writing your document template…',
  editable = true,
}) {
  const [showVarMenu, setShowVarMenu] = useState(false);
  const [newVarName, setNewVarName] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TemplateVariable,
    ],
    content: prepareHtmlForEditor(content),
    editable,
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || !content) return;
    const current = editor.getHTML();
    if (current !== content && current !== prepareHtmlForEditor(content)) {
      editor.commands.setContent(prepareHtmlForEditor(content));
    }
  }, [editor, content]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  const insertVariable = (name) => {
    if (!editor) return;
    editor.chain().focus().insertVariable(name).run();
    if (!variables.includes(name) && onVariablesChange) {
      onVariablesChange([...variables, name].sort());
    }
    setShowVarMenu(false);
  };

  const addCustomVariable = () => {
    const cleaned = newVarName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');
    if (!cleaned) return;
    insertVariable(cleaned);
    setNewVarName('');
  };

  if (!editor) return null;

  return (
    <div className="rich-text-editor border border-slate-300 rounded-sm bg-white flex flex-col">
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <Bold size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <Italic size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <UnderlineIcon size={15} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
            <Heading1 size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
            <Heading2 size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
            <Heading3 size={15} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
            <List size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
            <ListOrdered size={15} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">
            <AlignLeft size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center">
            <AlignCenter size={15} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">
            <AlignRight size={15} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            title="Insert table"
          >
            <TableIcon size={15} />
          </ToolbarButton>
          {editor.isActive('table') && (
            <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">
              <Trash2 size={15} />
            </ToolbarButton>
          )}

          <ToolbarDivider />

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVarMenu(v => !v)}
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-colors"
              data-testid="insert-variable-btn"
            >
              <Variable size={14} /> Insert Variable <ChevronDown size={12} />
            </button>
            {showVarMenu && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-sm shadow-lg w-56 py-1">
                {variables.map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => insertVariable(v)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-violet-50 text-slate-700"
                  >
                    <span className="variable-chip text-xs">{`{{${v}}}`}</span>
                  </button>
                ))}
                <div className="border-t border-slate-100 mt-1 pt-1 px-2 pb-1">
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newVarName}
                      onChange={e => setNewVarName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomVariable())}
                      placeholder="custom_variable"
                      className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                    <button type="button" onClick={addCustomVariable} className="p-1 text-violet-600 hover:bg-violet-50 rounded-sm">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <EditorContent editor={editor} className="tiptap-content flex-1" />
    </div>
  );
}
